import { Component } from '@angular/core';
import { ModulesService } from '../../services/modules.service';
import { firstValueFrom } from 'rxjs';
import { MetaModule } from '../../models/MetaModule';
import { CommonModule } from '@angular/common';
import { Module } from '../../models/Module';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { Level, ScoresData } from '../../models/ScoresResponse';
import { User, UserTier } from '../../models/User';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { Role } from '../../models/Role';
import { PaymentModalComponent } from '../modales/payment-modal/payment-modal.component';
import { ModalService } from '../../services/modal.service';
import { ModuleAccess } from '../../models/ModuleAccess';
import { OfferBannerComponent } from '../offer-banner/offer-banner.component';
import { TranslateModule } from '@ngx-translate/core';
import { PremiumOfferService } from '../../services/premium-offer.service';

@Component({
    selector: 'app-parcours-teacher-parent',
    standalone: true,
    imports: [
        CommonModule,
        NgbCollapseModule,
        OfferBannerComponent,
        TranslateModule,
    ],
    templateUrl: './parcours-teacher-parent.component.html',
    styleUrl: './parcours-teacher-parent.component.scss',
})
export class ParcoursTeacherParentComponent {
    user?: User;
    scores?: ScoresData;
    meta?: MetaModule[];
    levels: Level[] = [Level.bronze, Level.argent, Level.or, Level.diamant];
    moduleAccess: ModuleAccess[] = [];
    shouldDisplayPremiumOffer: boolean = false;

    constructor(
        private router: Router,
        private authService: AuthService,
        private moduleService: ModulesService,
        private modalService: ModalService,
        private premiumOfferService: PremiumOfferService
    ) {}

    async ngOnInit() {
        const user = await firstValueFrom(
            this.authService.getUserInfoOrRefreshIfNeeded()
        );
        const scores = await firstValueFrom(
            this.authService.getMyScores(Role.professeur)
        );
        const meta = await firstValueFrom(this.moduleService.getMetaModules());
        const moduleAccess: ModuleAccess[] = await firstValueFrom(
            this.moduleService.getModulesAccess()
        );

        this.user = user;
        this.moduleAccess = moduleAccess;
        this.scores = scores.data[0];
        this.meta = meta;
        this.shouldDisplayPremiumOffer =
            this.premiumOfferService.canSeePremiumOffers(user, {
                includeTeachers: true,
            });
    }

    getModuleImg(module: Module) {
        return module.illustration_url;
    }

    shouldDisplayMedal(module: Module): boolean {
        return this.getMedalString(module) !== 'bois';
    }

    private getMedalString(module: Module): string {
        let medalPath = 'bois';

        if (this.scores) {
            let progress = this.scores.progresses.find(
                (elt) => elt.module_id === module.module_enum
            );

            if (progress) {
                if (progress.validated_leagues.includes(Level.diamant)) {
                    medalPath = 'diamant';
                } else if (progress.validated_leagues.includes(Level.or)) {
                    medalPath = 'or';
                } else if (progress.validated_leagues.includes(Level.argent)) {
                    medalPath = 'argent';
                } else if (progress.validated_leagues.includes(Level.bronze)) {
                    medalPath = 'bronze';
                }
            }
        }

        return medalPath;
    }

    medalForModule(module: Module): string {
        let medalPath = this.getMedalString(module);
        let imgPath = `assets/levels/done/${medalPath}.png`;

        return imgPath;
    }

    async playModule(meta: MetaModule, module: Module, event: MouseEvent) {
        await this.checkFromUserTierBeforeNavigation(meta, module);
    }

    getBackgroundColorForCard(meta: MetaModule, i: number): any {
        let obj: any = {
            'background-color': this.moduleService.getMetaModuleColor(
                meta.id,
                i
            ),
        };

        return obj;
    }

    getColorForCard(meta: MetaModule, i: number): any {
        let obj: any = {
            color: this.moduleService.getMetaModuleColor(meta.id, i),
        };

        return obj;
    }

    getBadge(level: Level, module: Module) {
        const levelPath: string = level;
        let progressPath = 'notdone';

        if (this.scores) {
            let progress = this.scores.progresses.find(
                (elt) => elt.module_id === module.module_enum
            );

            if (progress) {
                if (progress.validated_leagues.includes(level)) {
                    progressPath = 'done';
                }
            }
        }

        let path = `assets/levels/${progressPath}/${levelPath}.png`;

        return path;
    }

    progressStyleFor(module: Module): any {
        let obj: any = {
            width: `${this.progressFor(module)}%`,
        };

        return obj;
    }

    progressFor(module: Module): number {
        if (!this.scores) {
            return 0;
        }

        let progress = this.scores.progresses.find(
            (elt) => elt.module_id === module.module_enum
        );

        if (!progress) {
            return 0;
        }

        let percentage = 0;

        if (progress.validated_leagues.includes(Level.bronze)) {
            percentage = 25;
        }
        if (progress.validated_leagues.includes(Level.argent)) {
            percentage = 50;
        }
        if (progress.validated_leagues.includes(Level.or)) {
            percentage = 75;
        }
        if (progress.validated_leagues.includes(Level.diamant)) {
            percentage = 100;
        }

        return percentage;
    }

    checkFromUserTierBeforeNavigation(meta: MetaModule, module: Module) {
        const moduleEnum = module.module_enum;

        if (this.isContentLocked(module)) {
            this.openPaymentModal();
        } else {
            this.router.navigate([
                '/module_start_tp',
                this.moduleService.getMetaBgColor(meta.id),
                moduleEnum,
            ]);
        }
    }

    openPaymentModal() {
        const modalRef = this.modalService.open(PaymentModalComponent, {
            size: 'lg',
            backdrop: 'static',
            keyboard: false,
        });

        modalRef.result.then((result) => {
            if (result === 'cancel') {
                // ici
            }
        });
    }

    shouldDisplayPlayButton(module: Module): boolean {
        const moduleEnum = module.module_enum;
        const moduleScore = this.scores!.progresses.find(
            (elt) => elt.module_id === moduleEnum
        );
        const nextLevel = moduleScore?.next_level;

        return nextLevel !== null;
    }

    isContentLocked(module: Module): boolean {
        const moduleEnum = module.module_enum;
        const moduleScore = this.scores!.progresses.find(
            (elt) => elt.module_id === moduleEnum
        );
        const nextLevel = moduleScore?.next_level;

        if (!nextLevel) {
            return false;
        }

        const tier = this.user?.tier;

        if (tier === UserTier.premium) {
            return false;
        }

        const access = this.moduleAccess.find(
            (elt) => elt.module_enum === moduleEnum && elt.level === nextLevel
        );

        if (!access) {
            return true;
        }

        return access.tier === UserTier.premium;
    }
}
